import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import CurrencyUtil from "../../Helpers/CurrencyUtil";

// The optional fixed platform fee Australian merchants can add for each gift card on an order
// (GiftKard.Core/Helpers/PlatformFee.cs). The per-card amount comes from the server (settings.platformFee, 0 when the
// store doesn't charge one); an order's platformFee is the total the order was charged.

/** The gift cards an order pays the fee for: 1 for a top-up, otherwise the quantity bought (as StorefrontService counts). */
export const platformFeeGiftCardCount = (order: { paymentType?: string | null; topupGiftCardId?: string | null; quantity?: number | null }) =>
  order.paymentType === "Topup" || !!order.topupGiftCardId ? 1 : Math.max(order.quantity ?? 1, 1);

/** The fee for an order: the per-card fee times the gift cards, waived when nothing else is due, matching StorefrontService. */
export const calcPlatformFee = (platformFee: number | null | undefined, amountDueBeforeFee: number, giftCardCount: number) =>
  platformFee && platformFee > 0 && amountDueBeforeFee > 0 && giftCardCount > 0
    ? Math.round(platformFee * giftCardCount * 100) / 100
    : 0;

type PlatformFeeModalProps = {
  show: boolean;
  onHide: () => void;
  /** The fee per gift card. */
  unitAmount: number;
  /** The total fee on this order; omitted where there's no order yet (the "Platform fee applies" link). */
  orderAmount?: number;
  currencyCode?: string;
};

const PLATFORM_FEE_EXPLANATION =
  "This fee covers the cost of providing and operating the gift card platform, including secure online purchase, gift card delivery, and ongoing management of your gift card(s).";

export const PlatformFeeModal = (props: PlatformFeeModalProps) => (
  <Modal show={props.show} onHide={props.onHide} centered>
    <Modal.Header closeButton>
      <Modal.Title>Platform fee</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p className="text-sm">
        {props.orderAmount !== undefined
          ? `${CurrencyUtil.formatCurrency(props.orderAmount, props.currencyCode)} platform fee is applied to this order (${CurrencyUtil.formatCurrency(props.unitAmount, props.currencyCode)} per gift card). `
          : `A ${CurrencyUtil.formatCurrency(props.unitAmount, props.currencyCode)} platform fee per gift card is applied to each order. `}
        {PLATFORM_FEE_EXPLANATION}
      </p>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={props.onHide}>
        Close
      </Button>
    </Modal.Footer>
  </Modal>
);

/** The "Platform Fee ⓘ" order summary line: amount is the order's total fee, for giftCardCount gift cards. */
export const PlatformFeeSummaryLine = (props: { amount: number; giftCardCount: number; currencyCode?: string }) => {
  const [showModal, setShowModal] = useState(false);
  if (!props.amount) return null;

  return (
    <div className=" list-group-item d-flex align-items-center">
      <div className="flex-fill">
        <div className="d-block h6 font-bold mb-1">
          Platform Fee{" "}
          <button
            type="button"
            className="btn btn-link p-0 align-baseline text-muted"
            aria-label="What is the platform fee?"
            onClick={() => setShowModal(true)}
          >
            <i className="bi bi-info-circle"></i>
          </button>
        </div>
        <div className="d-flex mx-n1"></div>
      </div>
      <div className="ms-auto text-end">
        <span className="h6 font-semibold mb-1 mr-10">
          {CurrencyUtil.formatCurrency(props.amount, props.currencyCode)}
        </span>
      </div>
      <PlatformFeeModal
        show={showModal}
        onHide={() => setShowModal(false)}
        unitAmount={Math.round((props.amount / Math.max(props.giftCardCount, 1)) * 100) / 100}
        orderAmount={props.amount}
        currencyCode={props.currencyCode}
      />
    </div>
  );
};

/** The "Platform fee applies" link under the checkout card, for stores that charge the fee; amount is the per-card fee. */
export const PlatformFeeAppliesLink = (props: { amount?: number | null; currencyCode?: string }) => {
  const [showModal, setShowModal] = useState(false);
  if (!props.amount || props.amount <= 0) return null;

  return (
    <div className="text-center py-3">
      <button type="button" className="btn btn-link p-0 text-sm text-muted" onClick={() => setShowModal(true)}>
        Platform fee applies
      </button>
      <PlatformFeeModal
        show={showModal}
        onHide={() => setShowModal(false)}
        unitAmount={props.amount}
        currencyCode={props.currencyCode}
      />
    </div>
  );
};
